import React, { useMemo, useState } from 'react';
import { Alert, App, Button, Flex, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined, UploadOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useCrawlers } from '../../hooks/useCrawlers';
import { useCrawlersStore } from '../../stores/crawlers.store';
import AddCrawlerModal from '../../components/Crawlers/AddCrawlerModal.component';
import ImportCrawlerModal from '../../components/Crawlers/ImportCrawlerModal.component';
import CrawlerCodeModal from '../../components/Crawlers/CrawlerCodeModal.component';
import CrawlerChannelsDrawer from '../../components/Crawlers/CrawlerChannelsDrawer.component';
import {
  CrawlerEntity,
  CrawlerImportPayload,
  CrawlerStatus,
  canConfirmCrawlerCode,
  canReleaseCrawler,
  canRequestCrawlerCode,
  isCrawlerInCooldown,
} from '../../../domain/entities/Crawler';

import './AdminCrawlersPage.styles.css';

const STATUS_COLORS: Record<CrawlerStatus, string> = {
  new: 'default',
  code_sent: 'processing',
  active: 'success',
  paused: 'warning',
  releasing: 'cyan',
  released: 'purple',
  importing: 'processing',
  import_failed: 'error',
};

const AdminCrawlersPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { items, isLoading, isSaving, error, load, add, importFromSession, requestCode, confirmCode, release } = useCrawlers();

  const [isAddOpen, setAddOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [codeFor, setCodeFor] = useState<CrawlerEntity | undefined>();
  const [channelsFor, setChannelsFor] = useState<string | undefined>();
  const channelsCrawler = items.find((c) => c.uuid === channelsFor);

  const dateFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }), [i18n.language]);
  const dateTimeFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }), [i18n.language]);
  const formatDate = (value?: string) => (value ? dateFormat.format(new Date(value)) : '—');

  const showError = (e: unknown) => {
    const slug = e instanceof Error ? e.message : 'unknown-error';
    message.error(t(slug, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) }));
  };

  const run = async (action: () => Promise<unknown>, successKey: string): Promise<boolean> => {
    try {
      await action();
      message.success(t(successKey, { ns: 'messages' }));
      return true;
    } catch (e) {
      showError(e);
      return false;
    }
  };

  const openCode = (crawler: CrawlerEntity) => setCodeFor(crawler);

  const handleAdd = async (phone: string) => {
    let uuid = '';
    const added = await run(async () => {
      uuid = await add(phone);
    }, 'crawler-added');
    if (!added) return;
    setAddOpen(false);

    const sent = await run(() => requestCode(uuid), 'crawler-code-sent');
    const crawler = useCrawlersStore.getState().items.find((c) => c.uuid === uuid);
    if (sent && crawler) openCode(crawler);
  };

  const handleImport = async (payload: CrawlerImportPayload) => {
    if (await run(() => importFromSession(payload), 'crawler-imported')) setImportOpen(false);
  };

  const handleRequestCode = async (crawler: CrawlerEntity) => {
    if (await run(() => requestCode(crawler.uuid), 'crawler-code-sent')) openCode({ ...crawler, status: 'code_sent' });
  };

  const handleConfirm = async (crawler: CrawlerEntity, code: string, password: string) => {
    if (await run(() => confirmCode(crawler.uuid, code, password), 'crawler-authorized')) setCodeFor(undefined);
  };

  const handleResend = async (crawler: CrawlerEntity) => {
    await run(() => requestCode(crawler.uuid), 'crawler-code-sent');
  };

  const statusLabel = (status: CrawlerStatus) => t(`crawler-status-${status}`, { ns: 'labels' });

  const columns: ColumnsType<CrawlerEntity> = [
    {
      title: t('phone', { ns: 'labels' }),
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => <Typography.Text copyable>{phone}</Typography.Text>,
    },
    {
      title: t('crawler-status', { ns: 'labels' }),
      key: 'status',
      render: (_, crawler) => {
        const tag = <Tag color={STATUS_COLORS[crawler.status]}>{statusLabel(crawler.status)}</Tag>;
        const wrapped = crawler.source === 'imported'
          ? <Space size={4}>{tag}<Tag>{t('crawler-source-imported', { ns: 'labels' })}</Tag></Space>
          : tag;

        if (crawler.status === 'paused' && crawler.pausedReason) {
          return <Tooltip title={t(`crawler-paused-${crawler.pausedReason}`, { ns: 'labels', defaultValue: crawler.pausedReason })}>{wrapped}</Tooltip>;
        }
        if (crawler.status === 'import_failed' && crawler.importError) {
          return (
            <Tooltip title={t('crawler-import-error', { ns: 'labels', error: crawler.importError, count: crawler.importAttempts })}>
              {wrapped}
            </Tooltip>
          );
        }
        return wrapped;
      },
    },
    {
      title: t('crawler-age', { ns: 'labels' }),
      key: 'age',
      render: (_, crawler) => (
        <Tooltip
          title={
            <div>
              <div>{t('crawler-connected-at', { ns: 'labels' })}: {formatDate(crawler.connectedAt)}</div>
              <div>{t('crawler-telegram-known-since', { ns: 'labels' })}: {formatDate(crawler.telegramKnownSince)}</div>
            </div>
          }
        >
          <span>{t('crawler-age-days', { ns: 'labels', count: crawler.ageDays })}</span>
        </Tooltip>
      ),
    },
    {
      title: t('crawler-channels', { ns: 'labels' }),
      key: 'channels',
      render: (_, crawler) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => setChannelsFor(crawler.uuid)}>
          {crawler.pendingChannels > 0
            ? t('crawler-channels-count-pending', { ns: 'labels', listening: crawler.listeningChannels, pending: crawler.pendingChannels })
            : t('crawler-channels-count', { ns: 'labels', count: crawler.listeningChannels })}
        </Button>
      ),
    },
    {
      title: t('crawler-cooldown', { ns: 'labels' }),
      key: 'cooldown',
      render: (_, crawler) => (isCrawlerInCooldown(crawler) && crawler.cooldownUntil
        ? <Tag color="orange">{t('crawler-cooldown-until', { ns: 'labels', date: dateTimeFormat.format(new Date(crawler.cooldownUntil)) })}</Tag>
        : '—'),
    },
    {
      title: t('crawler-added-at', { ns: 'labels' }),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => formatDate(value),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, crawler) => (
        <Space wrap>
          {canConfirmCrawlerCode(crawler) && (
            <Button type="link" disabled={isSaving} onClick={() => openCode(crawler)}>{t('crawler-enter-code', { ns: 'buttons' })}</Button>
          )}
          {canRequestCrawlerCode(crawler) && (
            <Button type="link" disabled={isSaving} onClick={() => handleRequestCode(crawler)}>{t('crawler-request-code', { ns: 'buttons' })}</Button>
          )}
          {canReleaseCrawler(crawler) && (
            <Popconfirm
              title={t('crawler-release-confirm', { ns: 'modals' })}
              description={
                crawler.releaseNeedsWarning
                  ? <Typography.Text type="warning"><WarningOutlined /> {t('crawler-release-young', { ns: 'modals', count: crawler.ageDays })}</Typography.Text>
                  : t('crawler-release-description', { ns: 'modals' })
              }
              okText={t('crawler-release', { ns: 'buttons' })}
              cancelText={t('cancel', { ns: 'buttons' })}
              okButtonProps={{ danger: crawler.releaseNeedsWarning }}
              onConfirm={() => run(() => release(crawler.uuid), 'crawler-released')}
            >
              <Button type="link" disabled={isSaving}>{t('crawler-release', { ns: 'buttons' })}</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-crawlers-page">
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
        <div>
          <h1 className="admin-crawlers-title">{t('admin-crawlers', { ns: 'titles' })}</h1>
          <p className="admin-crawlers-subtitle">{t('admin-crawlers-subtitle', { ns: 'titles' })}</p>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => load()} loading={isLoading}>{t('refresh', { ns: 'buttons' })}</Button>
          <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>{t('crawler-import', { ns: 'buttons' })}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>{t('crawler-add', { ns: 'buttons' })}</Button>
        </Space>
      </Flex>

      {error && <Alert type="error" showIcon message={t(error, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) })} />}

      <Table<CrawlerEntity>
        rowKey={(crawler) => crawler.uuid}
        columns={columns}
        dataSource={items}
        loading={isLoading}
        scroll={{ x: 1000 }}
        pagination={false}
        rowClassName={(crawler) => (crawler.status === 'released' ? 'admin-crawlers-row-released' : '')}
        locale={{ emptyText: t('crawlers-empty', { ns: 'labels' }) }}
      />

      <AddCrawlerModal open={isAddOpen} isSaving={isSaving} onCancel={() => setAddOpen(false)} onSubmit={handleAdd} />

      <ImportCrawlerModal open={isImportOpen} isSaving={isSaving} onCancel={() => setImportOpen(false)} onSubmit={handleImport} />

      <CrawlerChannelsDrawer crawler={channelsCrawler} onClose={() => setChannelsFor(undefined)} />

      <CrawlerCodeModal
        crawler={codeFor}
        isSaving={isSaving}
        onCancel={() => setCodeFor(undefined)}
        onResend={handleResend}
        onSubmit={handleConfirm}
      />
    </div>
  );
};

export default AdminCrawlersPage;
